<template>
  <div class="layout-wrapper" :class="{ 'scrollable': !isLayoutLocked, 'countdown-locked': isLayoutLocked, 'countdown-mode': isCountdownMode }">
    <div v-if="isCountdownMode" class="monogram-wrapper">
      <div class="monogram-background"></div>
    </div>
    <AnnouncementBar v-if="!isCountdownMode" />
    <!-- Header -->
    <header>
      <div class="header-container">
        <!-- Hamburger Menu Button -->
        <button 
          class="hamburger-menu" 
          @click="toggleMobileMenu"
          :class="{ 'active': isMobileMenuOpen }"
          :aria-label="t('header.nav.toggle')"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <!-- Logo -->
        <div class="header-left">
          <router-link :to="isCountdownMode ? '/countdown' : '/'" class="logo-link">
            <img src="@/assets/images/logo.png" :alt="t('header.nav.logo_alt') || 'Logo'" class="logo">
          </router-link>
        </div>

        <!-- Desktop Navigation -->
        <nav class="header-center desktop-only" :class="{ 'disabled-nav': isCountdownMode }">
          <ul class="nav-links">
            <li v-if="!isCountdownMode">
              <router-link to="/" class="nav-link">{{ t('header.nav.home') }}</router-link>
            </li>
            <template v-for="category in categoryStore.mainCategories" :key="category.categoryId">
              <li v-if="category.isActive && !isCountdownMode">
                <router-link :to="category.slug" class="nav-link">{{ category.name }}</router-link>
              </li>
              <li v-else class="coming-soon">
                <span class="nav-link disabled">{{ category.name }}</span>
                <span class="badge">{{ t('product.coming_soon') }}</span>
              </li>
            </template>
          </ul>
        </nav>

        <!-- Right Menu -->
        <div class="header-right">
          <div class="lang-switcher" :class="{ 'desktop-only': !isCountdownMode }">
            <ConnectionIndicator minimal class="status-indicator-nav" />
            <button class="flag-btn" :class="{ active: currentLanguage === 'it' }" @click="setLanguage('it')" :title="t('header.nav.switch_it') || 'Italiano'">
              <svg viewBox="0 0 3 2"><rect width="1" height="2" fill="#008C45"/><rect width="1" height="2" x="1" fill="#F4F5F0"/><rect width="1" height="2" x="2" fill="#CD212A"/></svg>
            </button>
            <button class="flag-btn" :class="{ active: currentLanguage === 'en' }" @click="setLanguage('en')" :title="t('header.nav.switch_en') || 'English'">
              <svg viewBox="0 0 60 30"><path d="M0,0 v30 h60 v-30 z" fill="#012169"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" stroke-width="4"/><path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/><path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/></svg>
            </button>
          </div>

          <button v-if="!isAuthenticated && !isCountdownMode" @click="openLoginModal" class="login-btn">
            <span class="btn-text">{{ t('header.user.login') }}</span>
            <span class="btn-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </span>
          </button>

          <div v-else-if="isAuthenticated && !isCountdownMode" class="user-control" :class="{ 'countdown-user': isCountdownMode }" @mouseenter="onAvatarHover" @mouseleave="onAvatarLeave">
            <button class="avatar-btn">
              <span class="user-initials">{{ userInitials }}</span>
              <span class="status-indicator"></span>
            </button>
            <UserDropdown :isOpen="isUserMenuOpen" :user="authStore.user" :isLoggingOut="isLoggingOut" @close="onDropdownClose" @logout="handleLogout" @dropdown-hover="onDropdownHover" @dropdown-leave="onDropdownLeave" />
          </div>
          <div 
            v-if="!isCountdownMode" 
            class="cart-control"
          >
            <button class="cart-btn" @click="toggleCart">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18L19 21H5L3 6Z" />
                <path d="M9 6V4a3 3 0 0 1 6 0v2" />
              </svg>
              <span class="count-badge">{{ cartCount }}</span>
            </button>
            <CartPreview 
              :isOpen="isCartOpen" 
              :cartItems="cartItems" 
              :subtotal="subtotal" 
              :listSubtotal="listSubtotal"
              :totalSavings="totalSavings"
              :cartCount="cartCount"
              :shipping="shipping"
              :total="total"
              @close="closeCart"
            />
          </div>
        </div>
      </div>
    </header>

    <!-- Mobile Navigation Menu -->
    <nav class="mobile-nav-overlay" :class="{ 'mobile-open': isMobileMenuOpen, 'countdown-mode-nav': isCountdownMode }">
      <ul class="nav-links">
        <li v-if="!isCountdownMode">
          <router-link to="/" class="nav-link" @click="closeMobileMenu">{{ t('header.nav.home') }}</router-link>
        </li>
        <template v-for="category in categoryStore.mainCategories" :key="category.categoryId">
          <li v-if="category.isActive && !isCountdownMode">
            <router-link :to="category.slug" class="nav-link" @click="closeMobileMenu">{{ category.name }}</router-link>
          </li>
          <li v-else class="coming-soon">
            <span class="nav-link disabled">{{ category.name }}</span>
            <span class="badge">{{ t('product.coming_soon') }}</span>
          </li>
        </template>
        <!-- Selettore Lingua Mobile -->
        <li class="mobile-lang-item">
          <div class="lang-switcher">
            <button class="flag-btn" :class="{ active: currentLanguage === 'it' }" @click="setLanguage('it')">
              <svg viewBox="0 0 3 2"><rect width="1" height="2" fill="#008C45"/><rect width="1" height="2" x="1" fill="#F4F5F0"/><rect width="1" height="2" x="2" fill="#CD212A"/></svg>
              <span>IT</span>
            </button>
            <button class="flag-btn" :class="{ active: currentLanguage === 'en' }" @click="setLanguage('en')">
              <svg viewBox="0 0 60 30"><path d="M0,0 v30 h60 v-30 z" fill="#012169"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" stroke-width="4"/><path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/><path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/></svg>
              <span>EN</span>
            </button>
          </div>
        </li>
      </ul>
    </nav>

    <!-- Page Content -->
    <main class="main-content">
      <router-view />
    </main>

    <!-- Footer -->
    <Footer :is-countdown-mode="isCountdownMode" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from '@/composables/useToast'
import { useCart } from '@/composables/useCart'
import { useLanguage } from '@/composables/useLanguage'
import { useCategoryStore } from '@/stores/categoryStore'
import { useConfigStore } from '@/stores/configStore'
import Footer from '../components/Footer.vue'
import CookieBanner from '../components/CookieBanner.vue'
import UserDropdown from '../components/UserDropdown.vue'
import CartPreview from '../components/CartPreview.vue'
import AnnouncementBar from '../components/AnnouncementBar.vue'
import ConnectionIndicator from '../components/ConnectionIndicator.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const categoryStore = useCategoryStore()
const configStore = useConfigStore()
const toast = useToast()
const { cartCount, cartItems, subtotal, listSubtotal, totalSavings, shipping, total, isCartOpen, openCart, closeCart, toggleCart } = useCart()
const { t, currentLanguage, setLanguage } = useLanguage()

const isCountdownMode = computed(() => configStore.isCountdownMode)
const isLayoutLocked = computed(() => isCountdownMode.value && route.name === 'Countdown')
const isMobileMenuOpen = ref(false)

watch(isMobileMenuOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const toggleCountdownMode = () => {
  if (configStore.isCountdownMode) {
    router.push('/')
    configStore.isCountdownMode = false
  } else {
    router.push('/countdown')
    configStore.isCountdownMode = true
  }
}

watch(() => route.name, () => {
  closeMobileMenu()
})

const openLoginModal = () => {
  router.push('/login')
}

const openForgotPasswordModal = () => {
  router.push('/forgot-password')
}

const handleShowRegistration = () => {
  router.push('/register')
}

const isUserMenuOpen = ref(false)
const isLoggingOut = ref(false)
const isAvatarHovering = ref(false)
const isDropdownHovering = ref(false)
let hoverTimeout = null

const isAuthenticated = computed(() => authStore.isAuthenticated)

const userInitials = computed(() => {
  if (!authStore.user) return ''
  const first = authStore.user.firstName?.charAt(0) || ''
  const last = authStore.user.lastName?.charAt(0) || ''
  return (first + last).toUpperCase()
})

onMounted(() => {
  categoryStore.fetchCategories(currentLanguage.value)
  
  if (route.query.showLogin === 'true' && !isAuthenticated.value && !isCountdownMode.value) {
    router.push('/login')
  }
  
  window.addEventListener('open-registration', handleShowRegistration)
})

onUnmounted(() => {
  window.removeEventListener('open-registration', handleShowRegistration)
})

watch(() => route.query.showLogin, (newVal) => {
  if (newVal === 'true' && !isAuthenticated.value && !isCountdownMode.value) {
    router.push('/login')
  }
})

watch(currentLanguage, (newLang) => {
  categoryStore.fetchCategories(newLang)
})

const onAvatarHover = () => {
  isAvatarHovering.value = true
  if (hoverTimeout) clearTimeout(hoverTimeout)
  hoverTimeout = setTimeout(() => {
    isUserMenuOpen.value = true
  }, 200)
}

const onAvatarLeave = () => {
  isAvatarHovering.value = false
  if (hoverTimeout) clearTimeout(hoverTimeout)
  if (!isDropdownHovering.value) {
    hoverTimeout = setTimeout(() => {
      isUserMenuOpen.value = false
    }, 1500)
  }
}

const onDropdownClose = () => {
  isUserMenuOpen.value = false
}

const onDropdownHover = () => {
  isDropdownHovering.value = true
  if (hoverTimeout) clearTimeout(hoverTimeout)
}

const onDropdownLeave = () => {
  isDropdownHovering.value = false
  if (!isAvatarHovering.value) {
    hoverTimeout = setTimeout(() => {
      isUserMenuOpen.value = false
    }, 1500)
  }
}

const handleLogout = async () => {
  isLoggingOut.value = true
  try {
    await authStore.logout()
    isUserMenuOpen.value = false
    toast.success(t('toast.success.logout'))
    if (isCountdownMode.value) {
      await router.push('/countdown')
    } else {
      await router.push('/')
    }
  } finally {
    isLoggingOut.value = false
  }
}

// Espone toggleCountdownMode per uso globale (es. nel footer)
window.toggleCountdownMode = toggleCountdownMode
</script>

<style scoped>
.layout-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  padding-top: 115px;
  padding-bottom: env(safe-area-inset-bottom);
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.countdown-mode,
.countdown-locked {
  background-color: #000;
}

.countdown-mode {
  padding-top: 80px;
}

@media (max-width: 1024px) {
  .countdown-mode {
    padding-top: 70px;
  }
}

.countdown-mode header {
  top: 0 !important;
}

.countdown-locked {
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  display: flex;
  flex-direction: column;
  padding-top: 80px; /* Header senza announcement bar */
  position: relative;
  overflow: hidden;
  overscroll-behavior: none;
}

@media (max-width: 1024px) {
  .countdown-locked {
    padding-top: 70px;
  }
}

@media (min-width: 1025px) {
  .countdown-locked {
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    overflow: hidden;
    overscroll-behavior: none;
  }
}

@media (max-height: 550px) {
  .countdown-locked {
    display: block !important; /* Disabilita flex per evitare spinte verticali */
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow: visible !important;
    padding-top: 60px !important;
  }

  .countdown-locked .monogram-wrapper {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important; /* Lo sfondo si ferma ESATTAMENTE al footer */
    height: 100% !important;
    min-height: 100% !important;
    overflow: hidden !important;
    z-index: 0;
  }

  .countdown-locked .monogram-background {
    /* Ridotto al 120% in landscape per evitare scroll fantasma */
    width: 140% !important;
    height: 140% !important;
    top: -20% !important;
    left: -20% !important;
    max-height: none !important; 
  }
  
  .countdown-locked .main-content {
    display: block !important;
    height: auto !important;
    min-height: 0 !important;
    flex: none !important;
    position: relative;
  }

  .countdown-locked .footer {
    display: block !important;
    position: relative !important;
    margin-top: 0 !important;
    z-index: 20;
  }
}

.monogram-wrapper {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  background-color: #ffffff;
}

.monogram-background {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background-image: url("@/assets/images/logo.png");
  background-size: 120px auto;
  background-repeat: repeat;
  opacity: 0.22;
  transform: rotate(-15deg);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.footer {
  flex-shrink: 0;
  position: relative;
  z-index: 20;
  margin-top: auto;
}

.desktop-only {
  display: flex;
}

.mobile-only {
  display: none;
}

.btn-icon {
  display: none;
}

header {
  position: fixed;
  top: 35px;
  left: 0;
  width: 100%;
  height: 80px;
  background-color: #000000;
  z-index: 1000;
  border-bottom: 2px solid var(--teal);
  display: flex;
  align-items: center;
}

.header-container {
  width: 100%;
  max-width: 100%;
  padding: 0 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
}

.header-left {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 30px;
}

.logo-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
}

.logo {
  height: 45px;
  width: auto;
  object-fit: contain;
}

.logo-tag-text {
  font-family: 'Inter', sans-serif;
  font-size: 7px;
  font-weight: 700;
  letter-spacing: 2px;
  color: var(--dark-teal);
  margin-top: 4px;
  text-transform: uppercase;
  line-height: 1;
}

.header-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.nav-links {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 50px;
  align-items: center;
  flex-wrap: nowrap;
}

.nav-link {
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.nav-link:hover, .nav-link.router-link-exact-active {
  color: #fff;
}

/* Per le categorie, vogliamo che restino attive anche nelle sottopagine dei prodotti */
.nav-links li:not(:first-child) .nav-link.router-link-active {
  color: #fff;
}

.nav-link.disabled {
  color: rgba(255, 255, 255, 0.4);
  cursor: default;
}

.coming-soon {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.coming-soon .badge {
  font-size: 10px;
  color: var(--dark-teal);
  border: 1px solid var(--dark-teal);
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 700;
  transition: all 0.3s ease-in-out;
}

.coming-soon:hover .badge {
  background-color: var(--teal);
  color: #ffffff;
  box-shadow: 0 0 12px rgba(91, 166, 160, 0.6);
  transform: translateY(-1px);
}

.header-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 20px;
}

.lang-switcher {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-indicator-nav {
  margin-right: 5px;
}

.flag-btn {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.4);
  padding: 4px;
  cursor: pointer;
  width: 28px;
  opacity: 0.8;
  transition: opacity 0.3s, transform 0.2s ease-in-out, border-color 0.3s;
}

.flag-btn:hover {
  opacity: 1;
  transform: scale(1.1);
  border-color: rgba(91, 166, 160, 0.5);
}

.flag-btn.active {
  opacity: 1;
  border-color: var(--teal);
}

.flag-btn.active:hover {
  opacity: 1;
  transform: scale(1.1);
  border-color: var(--teal);
}

.flag-btn svg {
  width: 100%;
  display: block;
}

.login-btn {
  background: transparent;
  border: 1px solid var(--dark-teal);
  color: var(--dark-teal);
  padding: 6px 15px;
  border-radius: 0px;
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 1px;
}

.login-btn:hover {
  background-color: var(--teal);
  border-color: var(--teal);
  color: #000;
  box-shadow: none;
}

.user-control, .cart-control {
  position: relative;
}

.avatar-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  position: relative;
  cursor: pointer;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.3s ease;
}

.avatar-btn:hover {
  border-color: var(--teal);
  background: rgba(91, 166, 160, 0.1);
}

.user-initials {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.5px;
  line-height: 1;
}

.status-indicator {
  position: absolute;
  top: -1px;
  right: -1px;
  width: 10px;
  height: 10px;
  background: #28a745;
  border-radius: 50%;
  border: 2px solid #000;
}

.cart-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #fff;
  text-decoration: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.count-badge {
  position: absolute;
  bottom: -4px;
  right: -6px;
  background: var(--teal);
  color: #fff;
  font-size: 9px;
  font-weight: 600;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  line-height: 1;
}

.hamburger-menu {
  display: none;
}

/* Mobile Nav Overlay */
.mobile-nav-overlay {
  display: none;
  position: fixed;
  top: 0;
  margin-top: 115px; /* 35px announcement + 80px header */
  left: 0;
  width: 100%;
  height: calc(100vh - 115px);
  background: #000;
  z-index: 999;
  padding: 40px 30px;
  overflow-y: auto;
}

@media (max-width: 1024px) {
  .mobile-nav-overlay {
    margin-top: 105px; /* 35px announcement + 70px header */
    height: calc(100vh - 105px);
  }
  
  .mobile-nav-overlay.countdown-mode-nav {
    margin-top: 70px; /* Solo header */
    height: calc(100vh - 70px);
  }

  .mobile-nav-overlay.mobile-open {
    display: flex;
    flex-direction: column;
  }
}

.mobile-nav-overlay::before {
  content: '';
  position: absolute;
  top: -5px;
  left: 0;
  width: 100%;
  height: 5px;
  background: #000;
}

@media (max-width: 992px) {
  .nav-links {
    gap: 15px;
  }
  .nav-link {
    font-size: 16px;
  }
}

@media (max-width: 1024px) {
  .layout-wrapper {
    padding-top: 105px;
  }

  /* Override per la modalità countdown su mobile */
  .layout-wrapper.countdown-mode,
  .layout-wrapper.countdown-locked {
    padding-top: 70px !important;
  }

  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: flex;
  }

  header {
    height: 70px;
  }

  .header-container {
    padding: 0 20px;
  }

  .header-left {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1010; /* Sopra ad altri elementi della header */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-link {
    padding: 15px; /* Aumenta l'area di touch per mobile */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo {
    height: 35px;
  }

  .logo-tag-text {
    display: none;
  }

  .header-right {
    gap: 15px;
  }

  .countdown-locked .header-right {
    margin-left: auto;
  }

  .login-btn {
    background: none;
    border: none;
    padding: 0;
    color: #fff;
    min-width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .login-btn:hover {
    background: none;
    box-shadow: none;
    color: var(--teal);
  }

  .btn-text {
    display: none;
  }

  .btn-icon {
    display: block;
    line-height: 0;
  }

  .avatar-btn, .cart-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .nav-links {
    flex-direction: column;
    width: 100%;
    min-height: 100%;
    gap: 35px;
    align-items: center;
    padding-bottom: 40px;
  }

  .nav-link {
    font-size: 22px;
    letter-spacing: 3px;
  }

  .mobile-lang-item {
    margin-top: auto;
    padding-top: 30px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    width: 100%;
  }

  .mobile-lang-item .lang-switcher {
    width: 100%;
    justify-content: center;
    gap: 30px;
  }

  .mobile-lang-item .flag-btn {
    width: auto;
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 8px 15px;
    display: flex;
    align-items: center;
    gap: 10px;
    border-radius: 4px;
  }

  .mobile-lang-item .flag-btn svg {
    width: 20px;
  }

  .mobile-lang-item .flag-btn span {
    font-size: 14px;
    color: #fff;
    font-weight: 600;
  }

  .mobile-lang-item .flag-btn.active {
    border-color: var(--teal);
    background: rgba(91, 166, 160, 0.1);
  }

  .hamburger-menu {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    z-index: 1001;
    position: relative;
  }

  .hamburger-menu span {
    display: block;
    width: 24px;
    height: 1.5px;
    background: #fff;
    transition: all 0.3s ease;
  }

  .hamburger-menu span:nth-child(2) {
    margin: 6px 0;
  }

  .hamburger-menu.active span:nth-child(1) {
    transform: translateY(7.5px) rotate(45deg);
  }

  .hamburger-menu.active span:nth-child(2) {
    opacity: 0;
  }

  .hamburger-menu.active span:nth-child(3) {
    transform: translateY(-7.5px) rotate(-45deg);
  }
}
</style>